export type { Platform } from './detect';

/**
 * API platform code sent to backend (`Platform = Literal["tg","max","vk"]`).
 * Distinct from runtime detection `Platform` which also includes `'browser'`.
 */
export const PLATFORM_API_CODES = ['tg', 'vk', 'max'] as const;
export type ApiPlatform = typeof PLATFORM_API_CODES[number];

export interface ThemeParams {
  bg_color: string;
  text_color: string;
  button_color: string;
  button_text_color: string;
}

export interface PlatformAdapter {
  readonly platform: import('./detect').Platform;
  /**
   * API platform code for backend `/auth/init`. `null` for mock/browser
   * (auth bootstrap is unsupported and apiFetch must short-circuit).
   */
  readonly apiPlatform: ApiPlatform | null;
  /** Raw payload to send to `/auth/init` body as `init_data`. */
  getInitData(): string;
  showBackButton(onClick: () => void): void;
  hideBackButton(): void;
  /**
   * Show platform-native primary button. No-op on platforms without MainButton (mock/browser).
   */
  setMainButton(label: string, onClick: () => void): void;
  /** Hide platform-native primary button. */
  hideMainButton(): void;
  close(): void;
  hapticImpact(style?: 'light' | 'medium' | 'heavy'): void;
  hapticNotification(type: 'success' | 'error' | 'warning'): void;
  getThemeParams(): ThemeParams;
  share(text: string, url?: string): void;
  /** Open an external URL (deep-links, support links). */
  openExternal(url: string): void;
  /** Returns TG start_param (or URL ?start_param= for mock/browser). */
  getStartParam(): string | undefined;
  /**
   * Disable Telegram's native vertical-swipe-to-dismiss gesture (Bot API 7.7+).
   * Stops the app from closing/minimizing when the user pulls down on a short
   * screen (reflex pull-to-refresh) — internal `overflow-y` scroll is unaffected.
   * No-op on platforms/clients without the SDK method (mock, pre-7.7). See BUG-1157.
   */
  disableVerticalSwipes(): void;
  /** Read-only capability flags — `true` if the platform exposes a native equivalent. */
  readonly capabilities: {
    readonly nativeBackButton: boolean;
    readonly fileUpload: boolean;
  };
}

export class PlatformNotSupportedError extends Error {
  constructor(public readonly platform: string) {
    super(`Platform "${platform}" not supported yet`);
    this.name = 'PlatformNotSupportedError';
  }
}
